import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ChromeData, UserInfo } from '@amzn/aac-chrome-internal-api';
import { ErrorIdProvider } from '@amzn/storm-ui';
import {
  HeaderState,
  initHeaderState,
  updateUserInfo,
} from 'src/features/header/store/slice';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { ConnectedHeader } from 'src/features/header/Header';
import { getChromeData } from 'src/commons/apis';
import { HeaderMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { PageHeaderBundleProvider } from 'src/providers/PantherLocalizationProvider';
import { markComponentRendered } from 'src/utils/component-initialization';

//Deprecated: ax-chrome-header is not used and should not be used in any page assembling, this will cause exceed ChromeData API call
export default class AXChromeHeader extends HTMLElement {
  async _renderHeader() {
    if (window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__) {
      const hide = await window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__;
      if (hide) return;
    }
    markComponentStart(Components.HEADER);
    const endMarker = performanceMark('aac-chrome-ui-header');

    const getHeaderState = (chromeData: ChromeData): HeaderState => {
      return {
        ...chromeData.chromeHeaderData,
        ...chromeData.chromeSharedData,
      };
    };

    const initHeaderData = async () => {
      try {
        const chromeData = await getChromeData();
        const headerState = getHeaderState(chromeData);
        store.dispatch(initHeaderState(headerState));

        // merge loginContext to userInfo to fill in some missing info
        if (!headerState.userInfo.customerInfo.primaryEmail) {
          mergeUserInfo(headerState.userInfo);
        }

        HeaderMetricPublisher.publishCounterMonitor('initFailure', 0);
      } catch (e: any) {
        HeaderMetricPublisher.publishCounterMonitor('initFailure', 1);
        logger.error('failed to fetch chromeData:', e);
      }
    };

    const mergeUserInfo = (userInfo: UserInfo) => {
      const mergeCustomerInfo = () => {
        const loginContext = JSON.parse(
          sessionStorage.getItem('loginContext')!,
        );
        store.dispatch(
          updateUserInfo({
            ...userInfo,
            customerInfo: {
              fullName: loginContext.userName.value,
              primaryEmail: loginContext.userEmail.value,
            },
          }),
        );
      };

      if (sessionStorage.getItem('loginContext')) {
        mergeCustomerInfo();
      } else {
        window.addEventListener(
          'sessionStorage.loginContext',
          mergeCustomerInfo,
        );
      }
    };

    initHeaderData();

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name="Header">
                <PageHeaderBundleProvider>
                  <ConnectedHeader isWebComponentPath={true} />
                </PageHeaderBundleProvider>
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderHeader();
    markComponentRendered('header');
  }
}

if (!customElements.get('ax-chrome-header')) {
  customElements.define('ax-chrome-header', AXChromeHeader);
}
